/**
 * ===============================================================================
 * 🚀 REPOSITORIO: Catálogos desde BD (Tipos Empresa, Tamaños, etc.)
 * ===============================================================================
 *
 * Reemplaza: Constantes hardcodeadas en persona.ts
 *
 * Ubicación: infrastructure/adapters/catalogo/ (arquitectura hexagonal)
 *
 * Endpoints Backend:
 * - GET /api/catalogos/tipos-empresa
 * - GET /api/catalogos/tamanios-empresa
 */

import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Catalogo, CatalogoOption } from '../../../domain/model/catalogo/catalogo';
import { CatalogoRepository } from '../../../domain/service/catalogo/catalogo-repository';

@Injectable({ providedIn: 'root' })
export class HttpCatalogoRepository implements CatalogoRepository {

  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.backend.baseUrl}/servicios/catalogos`;

  // Caché en memoria para evitar consultas repetidas
  private tiposEmpresaCache = signal<CatalogoOption[]>([]);
  private tamaniosEmpresaCache = signal<CatalogoOption[]>([]);
  private tareasValidasExpedienteCache: string[] | null = null;
  private tiposCancelacionCache = signal<CatalogoOption[]>([]);
  private justificacionesCache = signal<CatalogoOption[]>([]);
  private motivosCancelacionCache = signal<CatalogoOption[]>([]);
  private cacheLoaded = {
    tiposEmpresa: false,
    tamaniosEmpresa: false,
    tiposCancelacion: false,
    justificaciones: false,
    motivosCancelacion: false
  };

  // =========================================================================
  // TIPOS DE EMPRESA (TPSC_TIPOS_EMPRESA)
  // =========================================================================

  /**
   * Obtiene tipos de empresa desde BD
   * Cachea el resultado para evitar consultas repetidas
   */
  async getTiposEmpresa(): Promise<CatalogoOption[]> {
    if (this.cacheLoaded.tiposEmpresa) {
      return this.tiposEmpresaCache();
    }

    try {
      const response = await firstValueFrom(
        this.http.get<Catalogo[]>(`${this.baseUrl}/tipos-empresa`)
      );

      const options = (response || []).map(item => ({
        codigo: item.codigo,
        descripcion: item.descripcion
      }));

      this.tiposEmpresaCache.set(options);
      this.cacheLoaded.tiposEmpresa = true;

      return options;
    } catch (error) {
      console.error('Error cargando tipos de empresa:', error);
      return [];
    }
  }

  // =========================================================================
  // TAMAÑOS DE EMPRESA (TPSC_TIPOS_TAMANIO_EMPRE)
  // =========================================================================

  /**
   * Obtiene tamaños de empresa desde BD
   * Cachea el resultado para evitar consultas repetidas
   */
  async getTamaniosEmpresa(): Promise<CatalogoOption[]> {
    if (this.cacheLoaded.tamaniosEmpresa) {
      return this.tamaniosEmpresaCache();
    }

    try {
      const response = await firstValueFrom(
        this.http.get<Catalogo[]>(`${this.baseUrl}/tamanios-empresa`)
      );

      const options = (response || []).map(item => ({
        codigo: item.codigo,
        descripcion: item.descripcion
      }));

      this.tamaniosEmpresaCache.set(options);
      this.cacheLoaded.tamaniosEmpresa = true;

      return options;
    } catch (error) {
      console.error('Error cargando tamaños de empresa:', error);
      return [];
    }
  }

  // =========================================================================
  // UTILIDADES
  // =========================================================================

  /**
   * Limpia la caché (útil para testing o refrescar datos)
   */
  clearCache(): void {
    this.tiposEmpresaCache.set([]);
    this.tamaniosEmpresaCache.set([]);
    this.tiposIdentificacionCache.set([]);
    this.tareasValidasExpedienteCache = null;
    this.tiposCancelacionCache.set([]);
    this.justificacionesCache.set([]);
    this.motivosCancelacionCache.set([]);
    this.cacheLoaded = {
      tiposEmpresa: false,
      tamaniosEmpresa: false,
      tiposCancelacion: false,
      justificaciones: false,
      motivosCancelacion: false
    };
  }

  // =========================================================================
  // TIPOS DE CANCELACIÓN DE CITA (TPSC_TIPOS_CANC_CITAS)
  // =========================================================================

  /**
   * Obtiene tipos de cancelación de cita desde BD
   * Códigos: CON (Consultor), PRO (Programador), más tipos de cliente
   *
   * <p>Legacy: Si SNAPLICA_CLIENTE='S' muestra todos activos,
   *           si no filtra los que tienen SNCLIENTE='N'</p>
   */
  async getTiposCancelacionCita(): Promise<CatalogoOption[]> {
    if (this.cacheLoaded.tiposCancelacion) {
      return this.tiposCancelacionCache();
    }

    try {
      const response = await firstValueFrom(
        this.http.get<Catalogo[]>(`${this.baseUrl}/tipos-cancelacion-cita`)
      );

      const options = (response || []).map(item => ({
        codigo: item.codigo,
        descripcion: item.descripcion,
        sncliente: item.sncliente || 'N'
      }));

      this.tiposCancelacionCache.set(options);
      this.cacheLoaded.tiposCancelacion = true;

      return options;
    } catch (error) {
      console.error('Error cargando tipos de cancelación:', error);
      return [];
    }
  }

  // =========================================================================
  // JUSTIFICACIONES DE CANCELACIÓN (TPSC_JUSTIFICACIONES)
  // =========================================================================

  /**
   * Obtiene justificaciones de cancelación desde BD
   * Requerido cuando el tipo de cancelador es "BES" (En Sitio)
   */
  async getJustificacionesCancelacion(): Promise<CatalogoOption[]> {
    if (this.cacheLoaded.justificaciones) {
      return this.justificacionesCache();
    }

    try {
      const response = await firstValueFrom(
        this.http.get<Catalogo[]>(`${this.baseUrl}/justificaciones-cancelacion`)
      );

      const options = (response || []).map(item => ({
        codigo: item.codigo,
        descripcion: item.descripcion
      }));

      this.justificacionesCache.set(options);
      this.cacheLoaded.justificaciones = true;

      return options;
    } catch (error) {
      console.error('Error cargando justificaciones:', error);
      return [];
    }
  }

  // =========================================================================
  // MOTIVOS DE CANCELACIÓN (TPSC_PARAMETROS_GENERALES)
  // =========================================================================

  /**
   * Obtiene motivos de cancelación desde BD
   * Tabla: TPSC_PARAMETROS_GENERALES (DSVALOR = 'stMotivo')
   * Campo obligatorio en el diálogo de cancelación de cita
   */
  async getMotivosCancelacion(): Promise<CatalogoOption[]> {
    if (this.cacheLoaded.motivosCancelacion) {
      return this.motivosCancelacionCache();
    }

    try {
      const response = await firstValueFrom(
        this.http.get<Catalogo[]>(`${this.baseUrl}/motivos-cancelacion`)
      );

      const options = (response || []).map(item => ({
        codigo: item.codigo,
        descripcion: item.descripcion
      }));

      this.motivosCancelacionCache.set(options);
      this.cacheLoaded.motivosCancelacion = true;

      return options;
    } catch (error) {
      console.error('Error cargando motivos de cancelación:', error);
      return [];
    }
  }

  // =========================================================================
  // TIPOS DE IDENTIFICACIÓN (TPSC_TIPOS_IDENTIFICACION)
  // =========================================================================

  private tiposIdentificacionCache = signal<CatalogoOption[]>([]);

  /**
   * Obtiene tipos de identificación desde BD
   * Tabla: TPSC_TIPOS_IDENTIFICACION
   * Legacy: ComunesFacade.listarTiposIdenti()
   * Usado para dropdowns de tipo de documento (CC, NIT, CE, etc.)
   */
  async getTiposIdentificacion(): Promise<CatalogoOption[]> {
    // Verificar si ya está en caché (usando el signal directamente)
    const cached = this.tiposIdentificacionCache();
    if (cached.length > 0) {
      return cached;
    }

    try {
      const response = await firstValueFrom(
        this.http.get<Catalogo[]>(`${this.baseUrl}/tipos-identificacion`)
      );

      const options = (response || []).map(item => ({
        codigo: item.codigo,
        descripcion: item.descripcion
      }));

      this.tiposIdentificacionCache.set(options);

      return options;
    } catch (error) {
      console.error('Error cargando tipos de identificación:', error);
      return [];
    }
  }

  /**
   * Obtiene los cdservicio que requieren Expediente.
   * Legacy: GeneralParameters.getParameterValue(GeneralParameters.TAREAS_VALIDAS_EXPEDIENTE)
   * Fuente: GET /api/servicios/catalogos/tareas-validas-expediente
   */
  async getTareasValidasExpediente(): Promise<string[]> {
    if (this.tareasValidasExpedienteCache !== null) {
      return this.tareasValidasExpedienteCache;
    }
    try {
      const lista = await firstValueFrom(
        this.http.get<string[]>(`${this.baseUrl}/tareas-validas-expediente`)
      );

      const normalizada = (lista ?? [])
        .map(v => String(v ?? '').trim())
        .filter(v => v.length > 0);
      this.tareasValidasExpedienteCache = normalizada;
      return normalizada;
    } catch (error) {
      console.error('Error cargando TAREAS_EXPEDIENTE:', error);
      return [];
    }
  }

  /**
   * Obtiene los motivos de rechazo de órdenes desde T094_ESTADOS (tipo 07).
   * Endpoint: GET /api/servicios/catalogos/motivos-rechazo-orden
   */
  async getMotivosRechazoOrden(): Promise<CatalogoOption[]> {
    try {
      const response = await firstValueFrom(
        this.http.get<Catalogo[]>(`${this.baseUrl}/motivos-rechazo-orden`)
      );

      return (response || []).map(item => ({
        codigo: item.codigo,
        descripcion: item.descripcion
      }));
    } catch (error) {
      console.error('Error cargando motivos de rechazo de orden:', error);
      return [];
    }
  }

  /**
   * Obtiene los controles de riesgo activos desde TPSC_CONTROL_RIESGO.
   * Endpoint: GET /api/servicios/catalogos/controles-riesgo
   * Legacy: SeguimientoRiesgoPotViewImpl.java / RegistroRiesgoPotViewImpl.java - dropdown lbTipoControl
   */
  async getControlesRiesgo(): Promise<CatalogoOption[]> {
    return this.cargarCatalogo('controles-riesgo', 'controles de riesgo');
  }

  /**
   * Agentes de riesgo — «Riesgo que le aplica» / «Riesgo identificado».
   * Tabla: TPSC_AGENTE_RIESGO
   * Legacy: dropdowns lbTipoRiesgo y lbRiesgoAplica del módulo de alertas tempranas
   */
  async getAgentesRiesgo(): Promise<CatalogoOption[]> {
    return this.cargarCatalogo('agentes-riesgo', 'agentes de riesgo');
  }

  /**
   * Estados del módulo de alertas tempranas, ya ordenados por el backend
   * (RPRP, RPE1, RPE2, RPE3, resto) igual que el legado.
   * Tabla: TPSC_ESTADOS
   */
  async getEstadosRiesgoPotencial(): Promise<CatalogoOption[]> {
    return this.cargarCatalogo('estados-riesgo-potencial', 'estados de riesgo potencial');
  }

  /**
   * Oficinas activas.
   * Tabla: TPSC_OFICINAS
   */
  async getOficinas(): Promise<CatalogoOption[]> {
    return this.cargarCatalogo('oficinas', 'oficinas');
  }

  /**
   * Tipos de empleado activos.
   * Tabla: TPSC_TIPOS_EMPLEADOS
   * Legacy: AdminEmpleadosViewImpl.java - dropdown lbTipoEmpleado (HU1259115)
   */
  async getTiposEmpleado(): Promise<CatalogoOption[]> {
    return this.cargarCatalogo('tipos-empleado', 'tipos de empleado');
  }

  /** Carga un catálogo simple y lo normaliza a {codigo, descripcion}. */
  private async cargarCatalogo(recurso: string, descripcionLog: string): Promise<CatalogoOption[]> {
    try {
      const response = await firstValueFrom(
        this.http.get<Catalogo[]>(`${this.baseUrl}/${recurso}`)
      );

      return (response || []).map(item => ({
        codigo: item.codigo,
        descripcion: item.descripcion
      }));
    } catch (error) {
      console.error(`Error cargando ${descripcionLog}:`, error);
      return [];
    }
  }
}
